import React, { useState } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useQuery } from 'react-query'
import { ArrowLeft, ExternalLink, AlertCircle, Shield, MessageSquare, Edit } from 'lucide-react'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import { buildPreviewDoc } from '../../utils/previewDoc'

// Live (published) URL — only valid once a site has been published
const getLiveUrl = (site) => {
  if (site.custom_domain) return `${site.custom_domain_ssl ? 'https' : 'http'}://${site.custom_domain}`
  if (site.azure_published_url) return site.azure_published_url
  const clientSlug = site.company_name?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'unknown'
  return `/sites/${clientSlug}/${site.slug}/`
}

const SitePreview = () => {
  const { siteId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  // Admin reaches this via /admin/sites/:id/preview, clients via /client/preview/:id.
  // Default the back target + actions to the right portal so neither 404s; an
  // explicit ?back= (e.g. from the admin client-detail page) overrides it.
  const isAdmin = location.pathname.startsWith('/admin')
  const backPath = searchParams.get('back') || (isAdmin ? '/admin/sites' : '/client/sites')
  const [iframeLoaded, setIframeLoaded] = useState(false)

  const { data: site, isLoading, isError } = useQuery(
    ['site-preview', siteId],
    () => apiService.get(`/sites/${siteId}`).then(r => r.data),
    { staleTime: 60000, retry: 1 }
  )

  // Page HTML lives in the `pages` table (the editor renders the home page), not
  // on the site row — fetch it so the preview matches what the editor shows.
  const { data: pages = [], isLoading: pagesLoading } = useQuery(
    ['site-preview-pages', siteId],
    () => apiService.get(`/sites/${siteId}/pages`).then(r => r.data.pages || []),
    { staleTime: 60000, retry: 1, enabled: !!siteId }
  )

  if (isLoading || pagesLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-0)' }}>
        <LoadingSpinner size="lg" text="Loading preview..." />
      </div>
    )
  }

  if (isError || !site) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 12, background: 'var(--bg-0)' }}>
        <AlertCircle style={{ width: 40, height: 40, color: 'var(--danger)' }} />
        <p style={{ color: 'var(--text-soft)' }}>Failed to load site preview.</p>
        <button className="btn" onClick={() => navigate(backPath)}>
          <ArrowLeft style={{ width: 14, height: 14 }} /> Go Back
        </button>
      </div>
    )
  }

  const homePage = pages.find(p => p.is_home) || pages[0]
  const previewDoc = buildPreviewDoc(homePage?.html_content, site.css_content, site.js_content)
  const isPublished = site.status === 'published' || !!site.azure_published_url || !!site.custom_domain
  const liveUrl = getLiveUrl(site)
  const displayDomain = site.custom_domain || site.azure_published_url?.replace(/^https?:\/\//, '') || site.slug || 'preview'

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-0)', zIndex: 50 }}>
      {/* Browser chrome */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-1)', flexShrink: 0 }}>
        <button className="site-action" title="Back" onClick={() => navigate(backPath)}>
          <ArrowLeft style={{ width: 14, height: 14 }} />
        </button>
        {/* Traffic lights */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: 'var(--danger)' }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: 'var(--warning)' }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: 'var(--success)' }} />
        </div>
        {/* URL bar */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 9, padding: '8px 14px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 999, maxWidth: 520, margin: '0 auto', fontSize: 12.5, color: 'var(--text-soft)', fontFamily: 'var(--font-mono)' }}>
          <Shield style={{ width: 12, height: 12, color: 'var(--success)', flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayDomain}{!isPublished && <span style={{ color: 'var(--text-faint)' }}> · draft preview</span>}
          </span>
        </div>
        {isPublished && (
          <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm">
            <ExternalLink style={{ width: 13, height: 13 }} /> Visit live site
          </a>
        )}
        {isAdmin ? (
          <button className="btn btn-sm btn-primary" onClick={() => navigate(`/admin/editor?site=${siteId}&client=${site.client_id}`)}>
            <Edit style={{ width: 13, height: 13 }} /> Open in editor
          </button>
        ) : (
          <button className="btn btn-sm btn-primary" onClick={() => navigate('/client/requests/new')}>
            <MessageSquare style={{ width: 13, height: 13 }} /> Request changes
          </button>
        )}
      </div>

      {/* Rendered content */}
      <div style={{ flex: 1, position: 'relative', background: 'white' }}>
        {previewDoc ? (
          <>
            {!iframeLoaded && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-0)' }}>
                <LoadingSpinner size="lg" text="Loading site..." />
              </div>
            )}
            <iframe
              srcDoc={previewDoc}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={`Preview: ${site.name}`}
              onLoad={() => setIframeLoaded(true)}
            />
          </>
        ) : (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: 'var(--bg-0)' }}>
            <AlertCircle style={{ width: 40, height: 40, color: 'var(--text-faint)' }} />
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontWeight: 500, color: 'var(--text-soft)', fontSize: 14, marginBottom: 4 }}>Nothing to preview yet</p>
              <p style={{ fontSize: 12, color: 'var(--text-faint)', maxWidth: 320 }}>This site doesn't have any content yet. Open the editor to start building it.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SitePreview
